import React, {useEffect, useState} from 'react';
import {Sidebar} from "primereact/sidebar";
import {useTranslate} from "../../../system/useTranslate";
import {ProgressBar} from "primereact/progressbar";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {DataView} from "primereact/dataview";
import {useDispatch, useSelector} from "react-redux";
import {useGoal} from "../../../models/goal/use-goal";
import {useProject} from "../../../models/project/use-project";
import {setWorkspaceMembership} from "../../../system/app-slice";
import BoxHeader from "../../components/box-header";
import {Button} from "primereact/button";
import {useNavigate} from "react-router-dom";
import ProjectsListDialog from "./projects-list-dialog";
import GoalsListDialog from "./goals-list-dialog";
import DatatableEmptyMessage from "../../../system/components/datatable-empty-message";

function GoalSidebar({goalData, setGoalData, onHide , disabled}) {
    const translate=useTranslate();
    const navigate=useNavigate();
    const goal = useGoal();
    return (
        <Sidebar showCloseIcon={false} className="w-3 rtl surface-50 text-xs headerless-sidebar" visible={goalData!==undefined} onHide={()=>{setGoalData(undefined); onHide?.()}} pt={{content: () => ({style: {padding: 0}})}}>
            <div className="flex flex-column gap-2 h-full">
                <div className="border-bottom-1 border-200 flex align-items-center justify-content-between p-2">
                    <div className="flex align-items-center gap-2">
                        <Icon icon="mi mi:close" onClick={()=>{setGoalData(undefined)}}/>
                        <span className="font-bold">{goalData?.title}</span>
                    </div>
                    <span className={goal.statusClassName(goalData?.goalStatus, true, true)}>{translate(goalData?.goalStatus)}</span>
                </div>
                <div className={`flex flex-column gap-3 flex-1 p-3 ${disabled && " p-disabled"}`}>

                    <GoalStatus goalData={goalData}/>

                    <CardView className="bg-white border-1 border-200">
                        <ZofinoProgressBar value={50} label={translate("overall-progress")}/>
                    </CardView>

                    <CardView className="bg-white border-1 border-200">
                        <LinkedGoals goalData={goalData}/>
                    </CardView>

                    <CardView className="bg-white border-1 border-200">
                        <LinkedProjects goalData={goalData}/>
                    </CardView>
                </div>
                <div className="p-3">
                    <Button disabled={disabled} onClick={()=>{navigate(`/goals/${goalData?.id}`)}} className="w-full border-round-lg text-xs p-2" icon={<Icon icon="mi mi:open_in_new"/>} label={translate("show-goal")}/>
                </div>
            </div>
        </Sidebar>
    );
}
function ZofinoProgressBar({value, label}) {
    return(
        <div className="flex flex-column p-3">
            <div className="flex justify-content-between align-items-center p-1 font-bold text-400">
                <span>{label}</span>
                <span>{value}%</span>
            </div>
            <ProgressBar showValue={false} style={{ height: '10px' }} value={value}/>
        </div>
    )
}

function GoalStatus({goalData}) {
    const translate = useTranslate();
    const goal = useGoal();
    const ItemTemplate = (data)=>{
        return(
            <div className="w-6 text-xs py-2">
                {data.render}
            </div>
        )
    }

    const cards = [
        {
            className: "flex align-items-center justify-content-center",
            render:(
                <div className="flex flex-column justify-content-center">
                    <span>{translate("goal-owner")}</span>
                    <div className="flex align-items-center gap-2">
                        <Avatar memberData={goalData?.owner?.member} className="text-3xs" style={{width:"1.55rem", height:"1.55rem"}}/>
                        <span className="font-bold text-2xs">{goalData?.owner?.member?.fullname}</span>
                    </div>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render:(
                <div className="flex flex-column justify-content-center">
                    <span>{translate("team")}</span>
                    <span className="font-bold p-2">{translate(goalData?.team?.name)}</span>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render:(
                <div className="flex flex-column justify-content-center">
                    <span>{translate("cycle")}</span>
                    <span className="font-bold">{goalData?.cycle?`${translate(goalData?.cycle?.range)} ${goalData?.cycle?.year}`:""}</span>
                </div>
            )

        },
        {
            className: "flex align-items-center justify-content-center",
            render:(
                <div className="flex flex-column justify-content-center">
                    <span>{translate("goal-status")}</span>
                    <span className={`font-bold ${goal.statusClassName(goalData?.goalStatus, true, true)}`}>{translate(goalData?.goalStatus)}</span>
                </div>
            )

        }
    ]

    return(
        <CardView className="bg-white border-1 border-200 p-3 flex flex-column gap-2">
            <div className="w-full flex justify-content-between align-items-center">
                <span>{translate("key-info")}</span>
            </div>
            <DataView emptyMessage={<DatatableEmptyMessage/>} itemTemplate={ItemTemplate} layout="grid" value={cards}/>
        </CardView>
    )
}

function LinkedProjects({goalData}) {
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const [projectsDialogVisible, setProjectsDialogVisible] = useState(false);
    const translate = useTranslate();
    const [projectsStats, setProjectsStats] = useState([])
    const goal = useGoal();
    const project = useProject();
    const ItemTemplate = (data)=>{
        return (
            <div className="w-full text-xs py-1">
                <div className="p-2 w-full border-round surface-100 flex flex-column gap-2">
                    <div className="flex justify-content-between">
                        <span>{data.title}</span>
                        <span className={project.statusClassName(data.projectStatus, true , true)}>{translate(data.projectStatus)}</span>
                    </div>
                    <ProgressBar className="w-full h-1rem text-2xs" showValue={true} value={data.progress.value}/>
                </div>
            </div>
        )
    }

    useEffect(() => {
        if (goalData){
            getLinkedProjects()
        }
    }, [goalData]);

    const updateGoal = async (goalData, updateData)=>{
        let _data = await goal.updateGoal(goalData?.documentId, updateData);
        let _goals = [...workspaceMembershipData?.workspace.goals];
        let _goalIndex = _goals.findIndex(g=>g.documentId===goalData.documentId);
        _goals[_goalIndex] = {...goalData, ..._data}
        dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, goals:_goals}}));
    }


    const getLinkedProjects = async ()=>{
        let _data = await project.getProjectsProgress({goals:{$in:[goalData?.id]}});
        setProjectsStats(_data);
    }

    return(
        <div>
            <div className="p-3 flex flex-column gap-2">
                <BoxHeader title={translate("goal-projects-progress")} />
                <DataView emptyMessage={<span className="text-xs text-300">{translate("no-project-linked")}</span>} itemTemplate={ItemTemplate} layout="list" value={projectsStats}/>
            </div>
            <ProjectsListDialog value={projectsStats} visible={projectsDialogVisible} onHide={()=>{setProjectsDialogVisible(false)}} onSelected={(e)=>{updateGoal(goalData, e.updatedData)}}/>
        </div>
    )
}

function LinkedGoals({goalData}) {
    const [goalsDialogVisible, setGoalsDialogVisible] = useState(false);
    const translate = useTranslate();
    const goal = useGoal();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ItemTemplate = (data)=>{
        return (
            <div className="w-full text-xs py-1">
                <div className="p-2 w-full border-round surface-100 flex justify-content-between align-items-center">
                    <div className="flex flex-column">
                        <span>{data?.title}</span>
                    </div>
                    <Button onClick={()=>{navigate(`/goals/${data.id}`)}} pt={{label:()=>({className:"font-normal text-2xs"})}} className="text-xs w-2 border-1 p-button-info p-button-outlined px-2 p-1" label={translate("show-goal")}/>
                </div>
            </div>
        )
    }

    const updateGoal = async (goalData, updateData)=>{
        let _data = await goal.updateGoal(goalData?.documentId, updateData);
        let _goals = [...workspaceMembershipData?.workspace.goals];
        let _goalIndex = _goals.findIndex(g=>g.documentId===goalData.documentId);
        _goals[_goalIndex] = {...goalData, ..._data}

        dispatch(setWorkspaceMembership({...workspaceMembershipData, workspace: {...workspaceMembershipData.workspace, goals:_goals}}));

    }

    return(
        <div>
            <div className="p-3 flex flex-column gap-2 ">
                <BoxHeader title={translate("goal-links")}/>
                <DataView emptyMessage={<span className="text-xs text-300">{translate("goal-no-link")}</span>} itemTemplate={ItemTemplate} layout="list" value={goalData?.parentGoal?[goalData?.parentGoal]:[]}/>
            </div>
            <GoalsListDialog goalData={goalData} value={goalData?.parentGoal} onSelected={(e)=>{updateGoal(goalData, e.updateData); setGoalsDialogVisible(false);}} visible={goalsDialogVisible} onHide={()=>{setGoalsDialogVisible(false)}}/>
        </div>
    )
}

function DataCard({label, children, className = "h-5rem"}) {
    return(
        <div className={`w-full text-xs ${className}`}>
            <div className="surface-100 border-round-2xl flex flex-column gap-2 p-2 h-full">
                {label&&<span className="font-bold text-400">{label}</span>}
                <div className="flex-1">
                    {children}
                </div>
            </div>
        </div>
    )
}

function CardView ({children, className}){
    return (
        <div className={`${className} text-xs w-full border-round-xl`}>
            {children}
        </div>
    )
}

export default GoalSidebar;
